import type { TOptions, TPlayer } from 'video.js'
import type { Events } from '|>/shared/events'

import videojs from 'video.js'
import 'videojs-contrib-eme' // oxfmt-ignore
import { MediaError } from '|>/shared/media-error'
import { supportedDrmsResolved } from '../drm'

//
// plugin documentation -> https://github.com/videojs/videojs-contrib-eme
//

type SourceMiddlewareNext = (err: unknown, source: Events.Media.Load) => void

function detectedKeySystems(
  drms: Awaited<typeof supportedDrmsResolved>
): string[] {
  const keys: string[] = []
  for (const drm of drms.values()) {
    keys.push(...drm)
  }
  return keys
}

/**
 * Source middleware that rejects DRM sources the current CDM cannot play.
 * Waits for CDM detection to settle so a still-empty list is not treated as
 * "unsupported" (that false-negative is DR001 / MEDIA_ERR_ENCRYPTED).
 */
export function createEmeSourceMiddleware(player: TPlayer) {
  let sourceGeneration = 0

  return {
    setSource: function (
      source: Events.Media.Load,
      next: SourceMiddlewareNext
    ) {
      // This solves the issue when switching channels with drm second channel just stuck in "seeking" state
      if (player.eme) player.eme.activeSrc = null

      const generation = ++sourceGeneration

      if (!(source && source.src && source.keySystems)) {
        next(null, source)
        return
      }

      const keySystems = source.keySystems

      supportedDrmsResolved.then((drms) => {
        if (player.isDisposed?.()) return
        // A newer src() superseded this request (channel switch / retry).
        if (generation !== sourceGeneration) return

        const supported = detectedKeySystems(drms)
        const isSupported = Object.keys(keySystems).some((keySystem) =>
          supported.includes(keySystem)
        )

        if (!isSupported) {
          // do NOT call next(), and call error after detection has settled
          player.error(MediaError.MEDIA_ERR_ENCRYPTED)
          return
        }

        next(null, source)
      })
    },
  }
}

// use middleware to check if source is supported
videojs.use('*', function (player) {
  return createEmeSourceMiddleware(player as unknown as TPlayer)
})

/**
 * Configure EME (Encrypted Media Extensions) plugin before player initialization
 */
export function configure(options: TOptions): TOptions {
  return options
}

const VJS_TECH_SELECTOR = '.vjs-tech'

/**
 * Workaround for videojs-contrib-eme when player is rendered in React Portal.
 *
 * Problem: The plugin calls player.$('.vjs-tech') to find the video element.
 * player.$() searches only within the player's DOM subtree. When the player
 * is rendered via createPortal(), the video can end up outside that subtree,
 * so player.$() returns null and the plugin crashes on null.tagName.
 *
 * Solution: Wrap player.$ and, when it returns null for .vjs-tech, return
 * player.tech_.el_ instead — the video element reference that video.js
 * stores internally regardless of DOM position.
 */
function ensureEmeFindsTechElement(player: TPlayer): void {
  const original$ = player.$
  if (!original$) return

  player.$ = function (selector: string, context?: Element) {
    const result = original$.call(this, selector, context)

    const isTechSelector = selector === VJS_TECH_SELECTOR
    const techElement = this.tech_?.el_
    const needsFallback = isTechSelector && !result && techElement

    if (needsFallback) {
      return techElement
    }

    return result
  }
}

/**
 * Injects EME (Encrypted Media Extensions) plugin and functionality into the player
 * It is required for playing DRM-protected content
 */
export function inject(player: TPlayer): TPlayer {
  ensureEmeFindsTechElement(player)

  // enable EME plugin
  player.eme()

  // re-enable EME plugin after player reset
  player.on('ready', () => {
    // `setupEmeListeners()` is used for manual setup for eme listeners after player.reset call
    // basically for any cases when player.tech.el is changed
    player.eme.setupEmeListeners()

    // `initLegacyFairplay()` is used to init the 'webkitneedskey' listener when using WebKitMediaKeys in Safari.
    // See https://github.com/videojs/videojs-contrib-eme?tab=readme-ov-file#initlegacyfairplay
    // This is useful because Safari currently supports both the modern `com.apple.fps` keysystem through MediaKeys
    // and the legacy `com.apple.fps.1_0` keysystem through WebKitMediaKeys.
    // Since this plugin will prefer using modern MediaKeys over WebkitMediaKeys initializing legacy fairplay
    // can be necessary for media using the legacy 1_0 keysystem.
    if (videojs.browser.IS_ANY_SAFARI || videojs.browser.IS_IOS) {
      player.eme.initLegacyFairplay()
    }
  })

  return player
}
